/* Salt XO — Early access sale inside the Zipify OCU cart drawer
 * ---------------------------------------------------------------------------
 * Companion to the "Salt Sale Discounts (WoW)" Shopify Function app. The app
 * decides the money: with the sale code applied, cart subtotal tiers give
 * 15% / 30% / 30% + free gifts, and Shopify applies that in the cart totals
 * and in checkout. What a discount function cannot do is put products in the
 * cart or explain the tiers to the shopper. This script does those two things
 * and nothing else:
 *
 *   1. A tier progress bar at the top of the OCU drawer ("Add $X more to
 *      unlock ...") while the sale code is applied to the cart.
 *   2. The free gift lines: added when the cart reaches the gift tier,
 *      removed when it drops below it or the code goes away. Lines it adds
 *      carry the hidden line property `_salt_gift`, and those are the only
 *      lines it ever removes.
 *
 * Settings: Theme settings → "Early access sale" (code, tiers, wording),
 * delivered to the browser by snippets/salt-early-access-config.liquid as a
 * JSON <script>. The tiers MUST match the app's config metafield — this
 * script only mirrors the app's rules; it never decides a price. The sale
 * code itself never reaches the page: Liquid ships its SHA-256 and the
 * codes applied to the cart are hashed here before comparing.
 *
 * Mirror of the app's engine (keep the two in step):
 *   - counted subtotal = sum of `original_line_price` over lines that are
 *     neither gift cards nor gift variants (plan price for subscriptions,
 *     before any discount) — the function's `cost.subtotalAmount`;
 *   - tier = highest threshold <= counted; thresholds are USD and scaled by
 *     Shopify.currency.rate, as the function scales by presentmentCurrencyRate;
 *   - gift lines are never counted; the function makes them free (up to the
 *     per-line cap) only at the gift tier.
 *
 * Contracts with Zipify's drawer (the same ones ocu-cart-subscription.js
 * relies on, re-verified 2026-10-22):
 *   - lines render as `.ocu-cart-line-item-wrapper[data-key]`; the bar is
 *     inserted as the first child of `.ocu-cart-content--container`;
 *   - Zipify patches window.fetch: any /cart/change, /cart/update or
 *     /cart/clear response (NOT /cart/add) re-renders the drawer and
 *     refreshes the `Zipify.Cart.cart` mirror. Gift removals therefore go
 *     through /cart/update.js, and that same request doubles as the drawer
 *     refresh after a /cart/add.js;
 *   - lifecycle events: ocu:cart:loaded, ocu:cart:opened, ocu:cart:update,
 *     ocu:cart:updated, ocu:product:added.
 *
 * Safety:
 *   - acts only when an applied code hashes to the configured one AND
 *     Shopify reports it applicable (a locked code applied by the wrong
 *     customer shows nothing and adds nothing);
 *   - one evaluation at a time; the cart is only changed after re-reading
 *     /cart.js, never from the mirror alone;
 *   - a refused add (sold out, unavailable) is remembered for 10 minutes and
 *     given up on after 3 failures per page load — no retry storms. Successful
 *     adds are never capped: a shopper can cross the gift tier as often as
 *     they like while adjusting the cart;
 *   - a gift the shopper removes while still earning it stays out for the
 *     session. "The shopper removed it" means exactly that: the gift line
 *     went while the rest of the cart stayed the same. A cleared cart, a
 *     completed checkout or a tier drop is not a dismissal, and gifts this
 *     script removed because the tier dropped come back when the tier is
 *     reached again;
 *   - the shopper's own lines are never touched. A gift product they added
 *     themselves is left alone (the function still makes it free at the tier).
 *
 * Debug: append ?salt_ea_debug to the URL (or set window.__saltEaDebug).
 * window.SaltEarlyAccessCart.{sync,disable}() and .stats/.state are exposed.
 * Kill switch without a deploy: Theme settings → Early access sale → Enable.
 */
(() => {
  'use strict';
  if (window.__saltEaInit) return;
  window.__saltEaInit = true;

  const cfgNode = document.getElementById('salt-ea-config');
  if (!cfgNode) return;
  let cfg;
  try {
    cfg = JSON.parse(cfgNode.textContent);
  } catch (err) {
    return;
  }

  const VERSION = '2026-10-22.5';
  const CODE_HASH = String(cfg.codeHash || '').toLowerCase();
  const PROP = '_salt_gift';
  const GIFT_QTY = Math.max(1, parseInt(cfg.giftQty, 10) || 1);
  const TIERS = (Array.isArray(cfg.tiers) ? cfg.tiers : [])
    .filter((t) => t && t.enabled !== false)
    .map((t) => ({
      threshold: Number(t.threshold),
      percent: Number(t.percent) || 0,
      label: String(t.label || '').trim(),
      gifts: String(t.gifts || '')
        .split(',')
        .map((s) => s.trim())
        .filter((s) => /^\d+$/.test(s)),
    }))
    .filter((t) => Number.isFinite(t.threshold) && t.threshold >= 0)
    .sort((a, b) => a.threshold - b.threshold);
  if (!/^[0-9a-f]{64}$/.test(CODE_HASH) || !TIERS.length) return;

  const TOP = TIERS[TIERS.length - 1];
  const GIFT_IDS = new Set(TIERS.flatMap((t) => t.gifts));
  const COPY = {
    eyebrow: 'Early access',
    progress: 'Add [amount] more to unlock [next]',
    progressCurrent: "You're getting [current]. Add [amount] more for [next].",
    top: "You've unlocked [current]",
    giftBadge: 'Free gift',
  };
  Object.entries(cfg.copy || {}).forEach(([key, value]) => {
    if (typeof value === 'string' && value.trim()) COPY[key] = value.trim();
  });
  const MONEY_FORMAT = String(cfg.moneyFormat || '${{amount}}');
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const DEBUG = /[?&]salt_ea_debug/.test(location.search) || Boolean(window.__saltEaDebug);
  const STORE_KEY = 'salt-ea-state';
  const RELOAD_KEY = 'salt-ea-reload';
  const UNAVAILABLE_TTL = 10 * 60 * 1000;
  const MAX_FAILURES = 3;
  const DRAWER_SEL = '.ocu-cart-drawer';
  const CONTENT_SEL = '.ocu-cart-drawer .ocu-cart-content--container';
  const WRAPPER_SEL = '.ocu-cart-line-item-wrapper[data-key]';
  const IS_CART_PAGE = /\/cart\/?$/.test(location.pathname);
  const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };

  const log = (...args) => {
    if (DEBUG) console.info('[salt-ea]', ...args);
  };
  const stats = { evaluations: 0, adds: 0, removals: 0, clamps: 0, refusals: 0, refreshes: 0, reloads: 0 };
  let disabled = false;

  // ---- session memory (private mode / blocked storage → stateless, still safe) ----
  const state = (() => {
    const blank = { added: [], dismissed: [], unavailable: {}, failures: {} };
    try {
      const saved = JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null');
      if (saved && typeof saved === 'object') {
        return {
          added: Array.isArray(saved.added) ? saved.added.map(String) : [],
          dismissed: Array.isArray(saved.dismissed) ? saved.dismissed.map(String) : [],
          unavailable: saved.unavailable && typeof saved.unavailable === 'object' ? saved.unavailable : {},
          failures: {}, // per page load on purpose
        };
      }
    } catch (err) {
      /* fall through */
    }
    return blank;
  })();
  function save() {
    try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify(state));
    } catch (err) {
      /* ignore */
    }
  }
  const isUnavailable = (id) => Date.now() - (Number(state.unavailable[id]) || 0) < UNAVAILABLE_TTL;

  // ---- sale code check (hash compare; the literal code is never in the page) ----
  const hashCache = new Map();
  async function sha256Hex(text) {
    if (hashCache.has(text)) return hashCache.get(text);
    let hex = '';
    try {
      const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
      hex = [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
    } catch (err) {
      /* no SubtleCrypto (plain http) → stays inert */
    }
    hashCache.set(text, hex);
    return hex;
  }
  async function saleCodeApplied(cart) {
    for (const entry of cart.discount_codes || []) {
      if (!entry || entry.applicable === false) continue;
      const code = String(entry.code || '').trim().toUpperCase();
      if (code && (await sha256Hex(code)) === CODE_HASH) return true;
    }
    return false;
  }

  // ---- money ----
  function formatMoney(cents) {
    const value = Math.max(0, Math.round(cents)) / 100;
    const [whole, frac] = value.toFixed(2).split('.');
    const group = (digits, sep) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    const noDecimals = String(Math.round(value));
    return MONEY_FORMAT.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return group(noDecimals, ',');
        case 'amount_with_comma_separator':
          return `${group(whole, '.')},${frac}`;
        case 'amount_no_decimals_with_comma_separator':
          return group(noDecimals, '.');
        case 'amount_with_apostrophe_separator':
          return `${group(whole, "'")}.${frac}`;
        case 'amount_with_space_separator':
          return `${group(whole, ' ')},${frac}`;
        case 'amount_no_decimals_with_space_separator':
          return group(noDecimals, ' ');
        default:
          return `${group(whole, ',')}.${frac}`;
      }
    });
  }

  // ---- cart access ----
  function mirror() {
    const cart = window.Zipify && window.Zipify.Cart && window.Zipify.Cart.cart;
    return cart && Array.isArray(cart.items) ? cart : null;
  }
  async function fetchCart() {
    try {
      const res = await fetch(`${ROOT}cart.js`, { cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!res.ok) return null;
      const cart = await res.json();
      return cart && Array.isArray(cart.items) ? cart : null;
    } catch (err) {
      return null;
    }
  }
  function waitIdle(maxMs = 1500) {
    return new Promise((resolve) => {
      const started = Date.now();
      const check = () => {
        const inst = window.Zipify && window.Zipify.Cart && window.Zipify.Cart.instance;
        if (!inst || !inst.processing || Date.now() - started > maxMs) return resolve();
        setTimeout(check, 150);
      };
      check();
    });
  }

  // ---- the engine mirror ----
  const toCents = (usd, rate) => Math.round(usd * 100 * rate);
  async function analyse(cart) {
    const rate = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
    let counted = 0;
    const giftLines = [];
    cart.items.forEach((item) => {
      const vid = String(item.variant_id);
      if (GIFT_IDS.has(vid)) {
        giftLines.push(item);
        return;
      }
      if (item.gift_card) return;
      counted += Number(item.original_line_price) || 0;
    });
    let current = null;
    let next = null;
    for (const tier of TIERS) {
      if (counted >= toCents(tier.threshold, rate)) current = tier;
      else {
        next = tier;
        break;
      }
    }
    const codeApplied = await saleCodeApplied(cart);
    return {
      cart,
      rate,
      counted,
      current,
      next,
      giftLines,
      codeApplied,
      itemCount: Number(cart.item_count) || 0,
      wanted: codeApplied && current ? current.gifts : [],
    };
  }

  // What the cart looked like at the end of the previous evaluation: the
  // non-gift lines and which gifts were in. A gift that is gone while the
  // non-gift lines are unchanged was removed on purpose by the shopper.
  let baseline = null;
  const signature = (cart) =>
    cart.items
      .filter((i) => !GIFT_IDS.has(String(i.variant_id)))
      .map((i) => `${i.key}:${i.quantity}`)
      .sort()
      .join('|');
  function snapshot(a) {
    baseline = { sig: signature(a.cart), gifts: new Set(a.giftLines.map((l) => String(l.variant_id))) };
  }

  function plan(a, fresh) {
    const present = new Set(a.giftLines.map((l) => String(l.variant_id)));
    if (fresh) {
      const sig = signature(a.cart);
      state.added = state.added.filter((id) => {
        if (present.has(id)) return true;
        const removedAlone = Boolean(baseline) && baseline.sig === sig && baseline.gifts.has(id);
        if (removedAlone && a.wanted.includes(id) && !state.dismissed.includes(id)) {
          state.dismissed.push(id);
          log('gift removed by the shopper, not re-adding this session', id);
        }
        return false; // gone for any other reason: just forget it
      });
      save();
    }
    const updates = {};
    const removedIds = [];
    const kept = new Set();
    let clamps = 0;
    a.giftLines.forEach((line) => {
      if (!line.properties || !line.properties[PROP]) return; // the shopper's own line
      const vid = String(line.variant_id);
      if (!a.wanted.includes(vid)) {
        updates[line.key] = 0;
        removedIds.push(vid);
      } else if (kept.has(vid)) {
        // A second line of the same gift (seen on staging after a quantity
        // change on the first): the app only makes one unit free, so the
        // extra line would be charged while wearing a free badge. Drop it.
        updates[line.key] = 0;
        clamps += 1;
      } else {
        kept.add(vid);
        if (Number(line.quantity) > GIFT_QTY) {
          updates[line.key] = GIFT_QTY;
          clamps += 1;
        }
      }
    });
    const adds = a.wanted.filter(
      (id) => !present.has(id) && !state.dismissed.includes(id) && !isUnavailable(id) && (state.failures[id] || 0) < MAX_FAILURES
    );
    return { adds, updates, removedIds, removals: removedIds.length, clamps, empty: !adds.length && !Object.keys(updates).length };
  }

  async function apply(p) {
    let touched = false;
    for (const id of p.adds) {
      try {
        const res = await fetch(`${ROOT}cart/add.js`, {
          method: 'POST',
          headers: JSON_HEADERS,
          body: JSON.stringify({ items: [{ id: Number(id), quantity: GIFT_QTY, properties: { [PROP]: '1' } }] }),
        });
        if (res.ok) {
          if (!state.added.includes(id)) state.added.push(id);
          stats.adds += 1;
          touched = true;
          log('gift added', id);
        } else {
          state.unavailable[id] = Date.now();
          state.failures[id] = (state.failures[id] || 0) + 1;
          stats.refusals += 1;
          const body = await res.json().catch(() => ({}));
          log('gift refused', id, res.status, body.description || body.message || '');
        }
      } catch (err) {
        state.failures[id] = (state.failures[id] || 0) + 1;
        stats.refusals += 1;
        log('gift add failed', id, err);
      }
    }
    save();
    const hasUpdates = Object.keys(p.updates).length > 0;
    if (!touched && !hasUpdates) return null;
    // One /cart/update.js carries the removals and clamps, and — because
    // Zipify re-renders on any /cart/update response — doubles as the drawer
    // refresh after the adds (/cart/add is not intercepted by them).
    try {
      const res = await fetch(`${ROOT}cart/update.js`, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ updates: p.updates }),
      });
      stats.refreshes += 1;
      if (res.ok) {
        const cart = await res.json();
        stats.removals += p.removals;
        stats.clamps += p.clamps;
        // Forget the gifts we just took out ourselves: otherwise, when the
        // tier is reached again, their absence would read as the shopper
        // having removed them and they would never come back.
        if (p.removedIds.length) {
          state.added = state.added.filter((id) => !p.removedIds.includes(id));
          save();
        }
        log('cart refreshed', { removals: p.removals, clamps: p.clamps });
        return cart && Array.isArray(cart.items) ? cart : null;
      }
      log('refresh refused', res.status);
    } catch (err) {
      log('refresh failed', err);
    }
    return null;
  }

  // ---- evaluation loop ----
  let busy = false;
  let dirty = false;
  let dirtyFetch = false;
  let pendingFetch = false;
  let evalTimer = null;
  let evaluated = false;
  let staleStrikes = 0;
  let watchdogQuietUntil = 0;
  let settleTimers = [];

  let pendingEval = false;
  function schedule(reason, fetchFresh) {
    if (disabled) return;
    if (fetchFresh) pendingFetch = true;
    pendingEval = true;
    clearTimeout(evalTimer);
    evalTimer = setTimeout(() => {
      const fresh = pendingFetch;
      pendingFetch = false;
      pendingEval = false;
      evaluate(reason, fresh);
    }, 200);
  }
  function scheduleSettle() {
    settleTimers.forEach(clearTimeout);
    settleTimers = [600, 2000].map((ms) => setTimeout(() => schedule('settle'), ms));
  }

  async function evaluate(reason, fetchFresh) {
    if (disabled) return;
    if (busy) {
      dirty = true;
      dirtyFetch = dirtyFetch || Boolean(fetchFresh);
      return;
    }
    busy = true;
    try {
      let fresh = false;
      let cart = fetchFresh ? null : mirror();
      if (!cart) {
        cart = await fetchCart();
        fresh = Boolean(cart);
      }
      if (!cart) return;
      evaluated = true;
      stats.evaluations += 1;
      let a = await analyse(cart);
      render(a);
      let p = plan(a, fresh);
      if (p.empty) {
        snapshot(a);
        return;
      }
      if (!fresh) {
        // About to change the cart: confirm against a fresh copy first. The
        // mirror can lag Zipify's own re-sync by a beat.
        cart = await fetchCart();
        if (!cart) return;
        a = await analyse(cart);
        render(a);
        p = plan(a, true);
        if (p.empty) {
          snapshot(a);
          staleStrikes += 1;
          if (staleStrikes >= 2) {
            watchdogQuietUntil = Date.now() + 60000;
            staleStrikes = 0;
            log('mirror disagreed with /cart.js twice; watchdog renders only for 60s');
          }
          return;
        }
      }
      staleStrikes = 0;
      log('acting', reason, { adds: p.adds, updates: p.updates });
      await waitIdle();
      const after = await apply(p);
      if (after) {
        const done = await analyse(after);
        render(done);
        snapshot(done);
        if (IS_CART_PAGE) reloadCartPage();
      } else {
        snapshot(a);
      }
      scheduleSettle();
    } catch (err) {
      log('evaluate failed', err);
    } finally {
      busy = false;
      if (dirty) {
        dirty = false;
        const fresh = dirtyFetch;
        dirtyFetch = false;
        schedule('queued', fresh);
      }
    }
  }

  function reloadCartPage() {
    try {
      const last = Number(sessionStorage.getItem(RELOAD_KEY)) || 0;
      if (Date.now() - last < 15000) return;
      sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    } catch (err) {
      return; // no storage → no reload guard → no reload
    }
    stats.reloads += 1;
    location.reload();
  }

  // ---- rendering ----
  let bar = null;
  function buildBar() {
    const el = document.createElement('div');
    el.className = 'salt-ea-bar';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    const eyebrow = document.createElement('p');
    eyebrow.className = 'salt-ea-bar__eyebrow';
    eyebrow.textContent = COPY.eyebrow;
    const text = document.createElement('p');
    text.className = 'salt-ea-bar__text';
    const track = document.createElement('div');
    track.className = 'salt-ea-bar__track';
    const fillNode = document.createElement('div');
    fillNode.className = 'salt-ea-bar__fill';
    track.appendChild(fillNode);
    TIERS.forEach((tier) => {
      if (tier.threshold <= 0 || tier === TOP || TOP.threshold <= 0) return;
      const tick = document.createElement('span');
      tick.className = 'salt-ea-bar__tick';
      tick.style.left = `${(tier.threshold / TOP.threshold) * 100}%`;
      tick.title = tier.label;
      track.appendChild(tick);
    });
    el.append(eyebrow, text, track);
    return el;
  }
  const fillTemplate = (template, vars) => String(template).replace(/\[(amount|next|current)\]/g, (m, key) => vars[key] || '');
  function describe(a) {
    const current = a.current ? a.current.label : '';
    if (!a.next) return { text: fillTemplate(COPY.top, { current }), pct: 100, unlocked: true };
    const nextCents = toCents(a.next.threshold, a.rate);
    const topCents = toCents(TOP.threshold, a.rate);
    const pct = topCents > 0 ? Math.max(0, Math.min(100, (a.counted / topCents) * 100)) : 0;
    const template = a.current && a.current.percent > 0 ? COPY.progressCurrent : COPY.progress;
    return {
      text: fillTemplate(template, { amount: formatMoney(nextCents - a.counted), next: a.next.label, current }),
      pct: Math.round(pct * 10) / 10,
      unlocked: false,
    };
  }
  function render(a) {
    if (disabled) return;
    decorateLines(a);
    const content = document.querySelector(CONTENT_SEL);
    if (!a.codeApplied || !a.itemCount || !content) {
      if (bar && bar.isConnected) bar.remove();
      return;
    }
    if (!bar) bar = buildBar();
    if (bar.parentElement !== content || content.firstElementChild !== bar) content.insertBefore(bar, content.firstChild);
    const d = describe(a);
    const textNode = bar.querySelector('.salt-ea-bar__text');
    if (textNode.textContent !== d.text) textNode.textContent = d.text;
    const fillNode = bar.querySelector('.salt-ea-bar__fill');
    const width = `${d.pct}%`;
    if (fillNode.style.width !== width) fillNode.style.width = width;
    if (bar.classList.contains('salt-ea-bar--unlocked') !== d.unlocked) bar.classList.toggle('salt-ea-bar--unlocked', d.unlocked);
  }
  function decorateLines(a) {
    const byKey = new Map(a.cart.items.map((l) => [l.key, l]));
    document.querySelectorAll(WRAPPER_SEL).forEach((wrapper) => {
      if (wrapper.querySelector('.ocu-ghost-product')) return;
      const line = byKey.get(wrapper.dataset.key);
      const vid = line ? String(line.variant_id) : '';
      const earned = Boolean(line) && a.wanted.includes(vid);
      const mode = !earned ? '' : line.properties && line.properties[PROP] ? 'free' : 'free-own';
      if ((wrapper.getAttribute('data-salt-ea-gift') || '') !== mode) {
        if (mode) wrapper.setAttribute('data-salt-ea-gift', mode);
        else wrapper.removeAttribute('data-salt-ea-gift');
      }
      const info = wrapper.querySelector('.ocu-item-info');
      const badge = wrapper.querySelector('.salt-ea-gift-badge');
      if (mode && info && !badge) {
        const b = document.createElement('span');
        b.className = 'salt-ea-gift-badge';
        b.textContent = COPY.giftBadge;
        info.appendChild(b);
      } else if (!mode && badge) {
        badge.remove();
      }
    });
  }

  let renderRaf = 0;
  function scheduleRender() {
    if (renderRaf) return;
    renderRaf = requestAnimationFrame(async () => {
      renderRaf = 0;
      if (disabled) return;
      const cart = mirror();
      if (cart) render(await analyse(cart));
    });
  }
  const observer = new MutationObserver((mutations) => {
    if (disabled) return;
    for (const m of mutations) {
      const target = m.target;
      if (target.nodeType === 1 && target.closest(DRAWER_SEL)) {
        scheduleRender();
        return;
      }
      for (const node of m.addedNodes) {
        if (node.nodeType === 1 && (node.matches(DRAWER_SEL) || node.querySelector(DRAWER_SEL))) {
          scheduleRender();
          return;
        }
      }
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  // Zipify's events are the primary trigger; the settle passes and the
  // watchdog are the nets for events that never fire (their crash paths).
  ['ocu:cart:update', 'ocu:cart:updated', 'ocu:cart:loaded'].forEach((name) => addEventListener(name, () => schedule(name)));
  ['ocu:cart:opened', 'ocu:product:added'].forEach((name) => addEventListener(name, () => schedule(name, true)));
  addEventListener('pageshow', (event) => {
    if (event.persisted) schedule('pageshow', true);
  });
  const watchdog = setInterval(() => {
    if (disabled) return;
    const drawer = document.querySelector(DRAWER_SEL);
    if (!drawer || !drawer.classList.contains('ocu-visible')) return;
    if (Date.now() < watchdogQuietUntil) scheduleRender();
    else schedule('watchdog');
  }, 4000);

  // Boot: Zipify may have loaded before this deferred script attached its
  // listeners; and with no Zipify at all (dead drawer, blocked script) the
  // gifts still have to be right for checkout, so /cart.js is the fallback.
  const inst = window.Zipify && window.Zipify.Cart && window.Zipify.Cart.instance;
  if (inst && inst.isLoaded) schedule('init');
  setTimeout(() => {
    if (!evaluated) schedule('fallback', true);
  }, 4000);
  if (IS_CART_PAGE) schedule('cart-page', true);

  window.SaltEarlyAccessCart = {
    version: VERSION,
    sync: () => schedule('manual', true),
    stats,
    state,
    tiers: TIERS,
    get busy() {
      return busy || dirty || pendingEval;
    },
    disable() {
      disabled = true;
      observer.disconnect();
      clearInterval(watchdog);
      clearTimeout(evalTimer);
      settleTimers.forEach(clearTimeout);
      if (bar) bar.remove();
      document.querySelectorAll('.salt-ea-gift-badge').forEach((el) => el.remove());
      document.querySelectorAll('[data-salt-ea-gift]').forEach((el) => el.removeAttribute('data-salt-ea-gift'));
      log('disabled');
    },
  };
  log('ready', VERSION, { tiers: TIERS, giftQty: GIFT_QTY });
})();
